import { useCallback, useContext, useState } from "react";
import { GlobalContext } from "../context/GlobalContext";

// calls POST /api/trace and keeps the results + request state
export default function useTraceSearch() {
  const { API_URL } = useContext(GlobalContext);

  const [records, setRecords] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searchId, setSearchId] = useState(0); // changes on every successful search
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { type, message }

  const search = useCallback(
    async ({ domain, type, values }) => {
      setError("");
      setResult(null);
      setLoading(true);
      const started = performance.now();

      try {
        const response = await fetch(`${API_URL}/api/trace`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ domain, search_type: type, q: values }),
        });

        let data;
        try {
          data = await response.json();
        } catch {
          throw new Error("Invalid response from server.");
        }

        // backend error shape: { success: false, message }
        if (!response.ok || (!Array.isArray(data) && data?.success === false)) {
          throw new Error(data?.message || "Something went wrong.");
        }

        // success shape: [ {...record} ]  (or { success: true, records: [...] })
        const list = Array.isArray(data) ? data : data?.records;
        if (!Array.isArray(list)) throw new Error("Unexpected response format.");

        setRecords(list);
        setSearched(true);
        setSearchId((n) => n + 1);

        const foundCount = list.filter((r) => r.stages?.[0]?.found).length;
        const secs = ((performance.now() - started) / 1000).toFixed(2);
        setResult({
          type: foundCount === list.length ? "success" : "warning",
          message: `${foundCount} of ${list.length} found in main DB (${secs}s)`,
        });
      } catch (err) {
        setError(err.message || "Unable to connect to the server.");
        setRecords([]);
        setSearched(false);
      } finally {
        setLoading(false);
      }
    },
    [API_URL]
  );

  return {
    records,
    searched,
    searchId,
    loading,
    error,
    result,
    search,
    clearError: () => setError(""),
    clearResult: () => setResult(null),
  };
}
